import type { Patient, PatchPatient } from "./calculateBasicInfoCompletion";
import type { QuickExposureSubstance } from "./types";

export const QUICK_EXPOSURE_SUBSTANCES: QuickExposureSubstance[] = [
  "alcohol",
  "cannabis",
  "cigarettes",
  "vape",
  "other",
];

type QuickExposureLifestylePatch = NonNullable<PatchPatient["lifestyle"]>;
type QuickExposureAddictionOperation = NonNullable<
  QuickExposureLifestylePatch["addictions"]
>[number];

function toQuickExposureSubstance(
  substance: unknown,
): QuickExposureSubstance | null {
  return QUICK_EXPOSURE_SUBSTANCES.includes(substance as QuickExposureSubstance)
    ? (substance as QuickExposureSubstance)
    : null;
}

function isCurrentAddiction(addiction: {
  frequency?: string | null;
  stopDate?: string | null;
}) {
  return addiction.frequency !== "stopped" && !addiction.stopDate;
}

/**
 * Treat an existing lifestyle answer as the source for the fast check. An
 * empty array is an explicit "none" answer; null means the patient has not
 * answered this checkpoint yet.
 */
export function getQuickExposureSubstances(
  patient: Patient | null | undefined,
): QuickExposureSubstance[] | null {
  const hasAddictions = patient?.lifestyle?.hasAddictions;
  if (hasAddictions == null) return null;

  const selected = new Set<QuickExposureSubstance>();
  for (const addiction of patient?.lifestyle?.addictions ?? []) {
    const substance = toQuickExposureSubstance(addiction.substance);
    if (substance && isCurrentAddiction(addiction)) {
      selected.add(substance);
    }
  }

  return QUICK_EXPOSURE_SUBSTANCES.filter((substance) =>
    selected.has(substance),
  );
}

/**
 * Persist the fast answer through the existing lifestyle/addictions model.
 * Deselecting a known current record marks it stopped, preserving its
 * detailed history instead of deleting it. New selections are deliberately
 * partial records; the full Substances flow collects the remaining details.
 */
export function buildQuickExposureLifestylePatch(
  patient: Patient | null | undefined,
  selectedSubstances: readonly QuickExposureSubstance[],
): QuickExposureLifestylePatch {
  const existingAddictions = patient?.lifestyle?.addictions ?? [];
  const selected = new Set(selectedSubstances);
  const operations: QuickExposureAddictionOperation[] = [];

  for (const addiction of existingAddictions) {
    const substance = toQuickExposureSubstance(addiction.substance);
    if (
      !substance ||
      !isCurrentAddiction(addiction) ||
      selected.has(substance)
    ) {
      continue;
    }

    operations.push({
      ...(addiction.id ? { id: addiction.id } : { substance }),
      frequency: "stopped",
    });
  }

  for (const substance of selectedSubstances) {
    const alreadyCurrent = existingAddictions.some(
      (addiction) =>
        toQuickExposureSubstance(addiction.substance) === substance &&
        isCurrentAddiction(addiction),
    );

    if (!alreadyCurrent) {
      operations.push({ substance, frequency: null });
    }
  }

  const patch: QuickExposureLifestylePatch = {
    hasAddictions:
      selectedSubstances.length > 0 || existingAddictions.length > 0,
  };

  if (patient?.lifestyle?.id) {
    patch.id = patient.lifestyle.id;
  }
  if (operations.length > 0) {
    patch.addictions = operations;
  }

  return patch;
}
